import { Box, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AsyncSelectScroll from "../../../../../../../../components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../../../components/CustomDatePicker";
import Flex from "../../../../../../../../components/Flex";
import Input from "../../../../../../../../components/Input";
import MaskMultiSelectScroll from "../../../../../../../../components/MaskMultiSelectScroll";
import NumberInput from "../../../../../../../../components/NumberInput";
import OutlineCard from "../../../../../../../../components/OutlineCard";
import ProviderSelectScroll from "../../../../../../../../components/ProviderSelectScroll";
import Text from "../../../../../../../../components/Text";
import DropZone from "../../../../../../../../components/app/DropZone";
import StyledButtonV2 from "../../../../../../../../components/app/StyledButtonV2";
import StyledModal from "../../../../../../../../components/globalModal/StyledModal";
import { useDebounce } from "../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import useGetCataLogCode from "../../../../../../../../queries/catalog/use-get-catalog-code";
import { useAuthorizationCreate } from "../../../../../../../../queries/patient-chart/demographics/authorization/use-authorization-create";
import { useAuthorizationUpdate } from "../../../../../../../../queries/patient-chart/demographics/authorization/use-authorization-update";
import { useAppointmentsV2 } from "../../../../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import { useInsurancePayerList } from "../../../../../../../../queries/use-insurance-payer";
import { formatDateTime } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  replaceUnderScoreAddSpace,
  showToastOnApiCall,
} from "../../../../../../../../utils/utils";
import { DOCUMENT_FORMAT_MAPPER } from "../../../patient-chart-new/document/document-constant";
import {
  AUTHORIZATION_FORM_INITIAL_VALUES,
  AuthorizationForm,
  AuthorizationValidationSchema,
  VISIT_COUNT_MAX,
  VISIT_COUNT_MIN,
} from "./authorization-constant";

interface UpsertAuthorizationModalProps {
  disClosureHost: DisclosureHost;
  title?: string;
}

const UpsertAuthorizationModal = ({
  disClosureHost,
  title,
}: UpsertAuthorizationModalProps) => {
  const { patientId } = useParams() as { patientId: any };
  const { mutateAsync: createAuthorization } = useAuthorizationCreate();
  const { mutateAsync: updateAuthorization } = useAuthorizationUpdate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();

  const [searchCptKey, setSearchCptKey] = useDebounce("");
  const [cptPagination, setCptPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: cptCodes, isFetching: isFetchingCptCodes } = useGetCataLogCode({
    pagination: cptPagination,
    searchKey: searchCptKey,
    type: "CPT",
    active: true,
    archive: false,
    enabled: disClosureHost?.opened,
  });

  const [isHovered, setIsHovered] = useState<string>();
  const [searchEncounterKey, setSearchEncounterKey] = useDebounce("");
  const [encounterPagination, setEncounterPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: patientEncounterList,
    isLoading: isLoadingEncounterList,
    isFetching: isFetchingEncounterList,
  } = useAppointmentsV2({
    pagination: encounterPagination,
    searchKey: searchEncounterKey,
    providerGroupId: getXtenantId(),
    appointmentStatus: ["COMPLETED"],
    // archieve: false,
    patientUuid: patientId,
  });

  const [searchInsKey, setSearchInsKey] = useDebounce("");
  const [insPagination, setInsPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: insurancePayerList,
    isLoading: insurancePayerListLoading,
    isFetching: insurancePayerListFetching,
  } = useInsurancePayerList({
    searchKey: searchInsKey,
    pagination: insPagination,
  });

  const handleSubmit = (values: AuthorizationForm) => {
    const payload = {
      priorAuthorizationId: (values as any)?.priorAuthorizationId ?? undefined,
      authorizationNumber: values.authorizationNumber,
      payers: values.payerId?.value ? [values.payerId.value] : [],
      patientId: patientId,
      startDate: values.startDate
        ? new Date(values.startDate).toISOString()
        : "",
      endDate: values.endDate ? new Date(values.endDate).toISOString() : "",
      allowedVisit: Number(values.allowedVisit),
      warningCount: Number(values.warningCount),
      remainingVisit: Number(values.remainingVisit),
      notes: values.notesComments,
      documentFormat: (values as any)?.documentFormat as any,
      cptCodes: values.cptCodes?.map((code) => code.value),
      requestingProviderId: values.requestingProvider?.value,
      providerId: values.requestingProvider?.value,
      encounterId: values.linkedEncounter
        ? JSON.stringify(values.linkedEncounter)
        : undefined,
      fileName: values.fileName,
      attachments: values.attachmentUpload ?? undefined,
    };

    showToastOnApiCall({
      apiCall: disClosureHost?.data
        ? updateAuthorization({
            authorization: payload as any,
            xTENANTID: getXtenantId(),
          })
        : createAuthorization({
            authorization: payload as any,
            xTENANTID: getXtenantId(),
          }),
      onSave: () => {
        handleClose();
        formik.resetForm();
        queryClient.invalidateQueries({
          queryKey: ["authorization-list"],
        });
      },
      showSnackbar,
    });
  };

  const formik = useFormik<AuthorizationForm>({
    enableReinitialize: true,
    initialValues: disClosureHost?.data || AUTHORIZATION_FORM_INITIAL_VALUES,
    validationSchema: AuthorizationValidationSchema,
    onSubmit: handleSubmit,
  });

  const transFormedCptCodes = useMemo(() => {
    const fetched =
      cptCodes?.data?.content?.map((it) => ({
        value: it?.uuid ?? "",
        label: `${it?.code} - ${it?.description || ""}`,
      })) ?? [];
    const selected = (formik.values?.cptCodes ?? []) as {
      [k: string]: string;
    }[];
    const idKey = Object.keys(fetched[0] ?? selected[0] ?? {})[0];
    const missing = selected.filter(
      (s) => !fetched.some((o) => o[idKey as keyof typeof o] === s[idKey])
    );
    return [...missing, ...fetched];
  }, [cptCodes, formik.values?.cptCodes]);

  const handleClose = () => {
    formik.resetForm();
    disClosureHost?.close();
  };

  return (
    <StyledModal
      open={disClosureHost.opened}
      onClose={handleClose}
      dialogTitle={title}
      dialogAction={
        <Flex gap={2}>
          <StyledButtonV2
            variant="secondary"
            label="Cancel"
            onClick={handleClose}
          />
          <StyledButtonV2
            variant="primary"
            label="Save"
            onClick={formik.submitForm}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "45vw",
          height: "70vh",
        },
      }}
    >
      <Grid container spacing={2}>
        {/* Patient Name */}
        {formik.values.patientName ? (
          <Grid item xs={6}>
            <Input
              label="Patient Name"
              textFieldProps={{
                name: "patientName",
                value: formik.values.patientName,
                disabled: true,
              }}
            />
          </Grid>
        ) : null}

        {/* Authorization Number */}
        <Grid item xs={6}>
          <Input
            required
            label="Authorization Number"
            textFieldProps={{
              name: "authorizationNumber",
              value: formik.values.authorizationNumber,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Enter the unique authorization ID from payer",
            }}
            error={
              formik.touched.authorizationNumber &&
              formik.errors.authorizationNumber
            }
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelectScroll
            required
            loading={insurancePayerListLoading || insurancePayerListFetching}
            pagination={insPagination}
            setPagination={setInsPagination}
            totalElements={insurancePayerList?.data?.page?.totalElements}
            value={formik.values.payerId}
            onChange={(opt) => {
              formik.setFieldValue("payerId", opt);
            }}
            onSearch={(v) => {
              setSearchInsKey(v);
              setInsPagination((p) => ({ ...p, page: 0 }));
            }}
            enableFilterOption
            options={insurancePayerList?.data?.content?.map((insurance) => ({
              value: insurance.payerId,
              label: insurance.payerName,
            }))}
            label="Payer Name"
            error={formik.touched.payerId && formik.errors.payerId}
          />
        </Grid>

        {/* Start Date */}
        <Grid item xs={6}>
          <CustomDatePicker
            required
            label="Start Date"
            name="startDate"
            value={formik.values.startDate}
            onChange={(val) => formik.setFieldValue("startDate", val)}
            handleBlur={formik.handleBlur}
            placeholder="Authorization start date"
            error={formik.touched.startDate && formik.errors.startDate}
          />
        </Grid>

        {/* End Date */}
        <Grid item xs={6}>
          <CustomDatePicker
            required
            label="End Date"
            name="endDate"
            value={formik.values.endDate}
            onChange={(val) => formik.setFieldValue("endDate", val)}
            handleBlur={formik.handleBlur}
            placeholder="Authorization end date"
            error={formik.touched.endDate && formik.errors.endDate}
          />
        </Grid>

        {/* CPT Codes */}
        <Grid item xs={6}>
          <MaskMultiSelectScroll
            required
            label="CPT Codes"
            options={transFormedCptCodes}
            placeholder="Select CPT Codes"
            value={formik.values?.cptCodes as any}
            onSearch={setSearchCptKey}
            pagination={cptPagination}
            setPagination={setCptPagination}
            totalElements={cptCodes?.data?.page?.totalElements}
            loading={isFetchingCptCodes}
            onChange={(v) => formik.setFieldValue("cptCodes", v)}
            error={formik.touched.cptCodes && (formik.errors.cptCodes as any)}
          />
        </Grid>

        {/* Allowed Visit */}
        <Grid item xs={6}>
          <NumberInput
            required
            label="Allowed Visit"
            min={VISIT_COUNT_MIN}
            max={VISIT_COUNT_MAX}
            blockedKeys={["-", "+", "e", "E", "."]}
            textFieldProps={{
              name: "allowedVisit",
              value: formik.values.allowedVisit,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Total visits authorized",
            }}
            error={formik.touched.allowedVisit && formik.errors.allowedVisit}
          />
        </Grid>

        {/* Warning Count */}
        <Grid item xs={6}>
          <NumberInput
            required
            label="Warning Count"
            min={VISIT_COUNT_MIN}
            max={VISIT_COUNT_MAX}
            blockedKeys={["-", "+", "e", "E", "."]}
            textFieldProps={{
              name: "warningCount",
              value: formik.values.warningCount,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Warn when remaining visits reach",
            }}
            error={formik.touched.warningCount && formik.errors.warningCount}
          />
        </Grid>

        {/* Remaining Visit */}
        <Grid item xs={6}>
          <NumberInput
            required
            label="Remaining Visit"
            min={VISIT_COUNT_MIN}
            max={VISIT_COUNT_MAX}
            blockedKeys={["-", "+", "e", "E", "."]}
            textFieldProps={{
              name: "remainingVisit",
              value: formik.values.remainingVisit,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Visits left under this authorization",
            }}
            error={
              formik.touched.remainingVisit && formik.errors.remainingVisit
            }
          />
        </Grid>

        {/* Requesting Provider */}
        <Grid item xs={6}>
          <ProviderSelectScroll
            required
            label="Requesting Provider"
            value={formik.values.requestingProvider}
            onChange={(v) => formik.setFieldValue("requestingProvider", v)}
            error={
              formik.touched.requestingProvider &&
              (formik.errors.requestingProvider as any)
            }
          />
        </Grid>

        {/* Notes/Comments */}
        <Grid item xs={12}>
          <Input
            label="Notes / Comments"
            textFieldProps={{
              name: "notesComments",
              value: formik.values.notesComments,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
              placeholder: "Enter notes / comments",
              multiline: true,
              rows: 3,
            }}
            error={formik.touched.notesComments && formik.errors.notesComments}
          />
        </Grid>

        {/* Linked Encounter */}
        <Grid item xs={12}>
          <AsyncSelectScroll
            enableFilterOption
            placeholder="Select Visit"
            label="Patient Visit"
            name="linkedEncounter"
            loading={isLoadingEncounterList || isFetchingEncounterList}
            pagination={encounterPagination}
            setPagination={setEncounterPagination}
            totalElements={patientEncounterList?.data?.page?.totalElements}
            onSearch={(v) => {
              setSearchEncounterKey(v);
              setEncounterPagination((p) => ({ ...p, page: 0 }));
            }}
            renderCustomLabel={(option) => {
              const HoveredBox = (label: any, time: any) =>
                `reason for visit: ${replaceUnderScoreAddSpace(
                  label
                )} Date & Time: ${formatDateTime(time)}`;

              return (
                <Box
                  onMouseEnter={() => setIsHovered(option.uuid)}
                  onMouseLeave={() => setIsHovered(undefined)}
                >
                  <Text>{replaceUnderScoreAddSpace(option.label)}</Text>
                  {isHovered === option.uuid && (
                    <OutlineCard>
                      {HoveredBox(option?.label ?? "", option?.startTime ?? "")}
                    </OutlineCard>
                  )}
                </Box>
              );
            }}
            options={
              patientEncounterList?.data?.content?.map((option) => {
                return {
                  value: option.uuid,
                  label: option?.chiefComplaint,
                  startTime: option?.encounterDate,
                };
              }) ?? []
            }
            value={formik.values.linkedEncounter as any}
            onChange={(value) => {
              formik.setFieldValue("linkedEncounter", value);
            }}
          />

          {/* Attachment Upload */}
          <Grid item xs={12} mt={1}>
            <Grid item xs={12} mt={2}>
              <DropZone
                supportedFile="PDF, JPG, PNG, DOCX"
                value={
                  typeof formik.values?.attachmentUpload === "string"
                    ? (formik.values.attachmentUpload as unknown as string)
                    : undefined
                }
                fileName={formik.values?.fileName}
                height={"243px"}
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                content="Drag & Drop files Or Browse Files"
                onClick={(file) => {
                  const curFile = file?.item(0);
                  formik.setFieldValue("attachmentUpload", curFile);
                  formik.setFieldValue("fileName", curFile?.name);

                  if (curFile?.type) {
                    formik.setFieldValue(
                      "documentFormat",
                      DOCUMENT_FORMAT_MAPPER?.[curFile.type] ?? "OTHER"
                    );
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </StyledModal>
  );
};

export default UpsertAuthorizationModal;
